import { Component } from "react"
import PropTypes from "prop-types"
import ReactTable from "react-table"
import WorldMapChart from "../charts/world_map"
import userGeoMetricsFetcher, { logScaleHack } from "./geo_fetcher"
import { fixedPercentValue } from "../lib"
import { capitalize, oxford } from "humanize-plus"
import TableIcon from "shared/components/icons/table_icon"
import MapIcon from "shared/components/icons/map_icon"

import statStyles from "../stats.scss"
import userStyles from "./user_metrics.scss"
import colors from "foundation/_colors_export.scss?variables"

const userTypes = [
  {
    name: "connected",
    description: "Users who have authenticated with your service",
  },
  {
    name: "active",
    description:
      "Users who have enabled 1 or more connections with your service",
  },
  {
    name: "power",
    description:
      "Users who have enabled 5 or more connections with your service",
  },
  {
    name: "disconnected",
    description: "Users who were formerly connected with your service",
  },
]

const descendingComparison = val => (first, second) => {
  let a = parseFloat((val ? first[val] : first).toString().replace("%", ""))
  let b = parseFloat((val ? second[val] : second).toString().replace("%", ""))
  if (b > a) return 1
  else if (a > b) return -1
  else return 0
}

export default class UserMetrics extends Component {
  state = {
    tableView: true,
    userType: userTypes[0].name,
    category: this.props.categories[0].id,
  }

  componentDidMount() {
    userGeoMetricsFetcher(this.props.serviceModuleName, this.state.userType, {
      currentMetricDate: this.props.currentMetricDate,
    }).then(data => this.setState({ geoData: data }))
  }

  updateAttribute = event =>
    this.setState({ [event.target.name]: event.target.value })

  toggleView = () => {
    this.setState({ tableView: !this.state.tableView })
  }

  geoInsight(data) {
    const trending = data
      .sort(descendingComparison("growthCount"))
      .filter(c => c.growthCount > 10)
      .slice(0, 3)

    if (trending.length)
      return (
        <div styleName="userStyles.insight">
          <h4>
            {this.props.serviceName} is trending in{" "}
            {oxford(trending.map(c => c.name))}
          </h4>
          <p styleName="userStyles.explanation">
            In the last 90 days, {this.props.serviceName} has seen a growth of{" "}
            {trending[0].growthPercentage}% of connected users in{" "}
            {trending[0].name}!
          </p>
        </div>
      )
  }

  render() {
    const { userType, geoData, tableView } = this.state
    return (
      <>
        <div styleName="userStyles.type-select">
          <h4 styleName="userStyles.geo-header">Select user type</h4>
          <select
            name="userType"
            styleName="userStyles.select"
            onChange={this.updateAttribute}
            defaultValue={userTypes[0].name}
          >
            {userTypes.map(type => (
              <option key={type.name} value={type.name}>
                {`${capitalize(type.name)}: ${type.description}`}
              </option>
            ))}
          </select>
        </div>
        <div styleName="statStyles.stats-container statStyles.user">
          <h1>By location</h1>
          {geoData && this.geoInsight(geoData)}
          <div styleName="userStyles.breakdown">
            <span>
              <h4>Breakdown by country</h4>
            </span>
            <span styleName="userStyles.toggles">
              <TableIcon
                onClick={this.toggleView}
                fill={
                  this.state.tableView ? "#0E9AF8" : colors.primaryTextColor
                }
              />
              <MapIcon onClick={this.toggleView} blue={!this.state.tableView} />
            </span>
          </div>
          {geoData &&
            (tableView ? (
              <div styleName="userStyles.top-countries userStyles.country-table">
                <ReactTable
                  data={geoData}
                  columns={[
                    {
                      Header: "Country",
                      accessor: "name",
                      Footer: "All",
                    },
                    {
                      id: "users",
                      Header: `${capitalize(userType)} Users`,
                      accessor: d => d[userType],
                      Footer: `${geoData.reduce(
                        (acc, cur) => acc + cur[userType],
                        0
                      )}`,
                    },
                    {
                      Header: `Total users in the ${this.props.categoryName} category`,
                      id: "addressable",
                      accessor: d => d.addressable + d.connected,
                      defaultSortDesc: true,
                    },
                    {
                      id: "penRate",
                      Header: "Service share",
                      accessor: d => {
                        let value = "-"
                        if (d[userType] && d.addressable) {
                          value = fixedPercentValue(
                            d[userType] / (d.addressable + d.connected)
                          )
                        }
                        return `${value}%`
                      },
                      defaultSortDesc: true,
                      sortMethod: descendingComparison(),
                    },
                  ]}
                  defaultSorted={[
                    {
                      id: "users",
                      desc: true,
                    },
                    {
                      id: "penRate",
                      desc: true,
                    },
                  ]}
                />
              </div>
            ) : (
              <WorldMapChart
                data={geoData.map(c => ({
                  value: logScaleHack(c[userType]),
                  name: c.name,
                  code: c.code,
                  categoryTotal: c.addressable + c.connected,
                }))}
                userType={userType}
                categoryName={this.props.categoryName}
                serviceName={this.props.serviceName}
              />
            ))}
        </div>
      </>
    )
  }
}

UserMetrics.propTypes = {
  categories: PropTypes.array,
  categoryName: PropTypes.string.isRequired,
  currentMetricDate: PropTypes.string.isRequired,
  serviceModuleName: PropTypes.string.isRequired,
  serviceName: PropTypes.string.isRequired,
  insight: PropTypes.string,
}

UserMetrics.defaultProps = {
  insight: `Sonos has 6242 connected users in the United States. There are a total of 30000 connected users in the United States in the "Music" category. The addressable market penetration of Sonos in the United States is 20% in the "Music" category`,
}
